import { Alert, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import StyledCheckbox from '@/components/StyledInputFields/StyledCheckbox.tsx';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea';
import useRerunFailedSteps from '@/hooks/api/runs/rerun/useRerunFailedSteps.ts';
import { Run } from '@/types/runs.ts';
import CacheBypassNotice from '@/components/CacheBypassNotice.tsx';

type RerunFailedStepsModalProps = {
  run: Run;
  opened: boolean;
  onClose: () => void;
};

const RerunFailedStepsModal = ({ run, opened, onClose }: RerunFailedStepsModalProps) => {
  const [ignoreCache, setIgnoreCache] = useState(false);
  const [targetStepFeedback, setTargetStepFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { mutate: rerunFailedSteps, isPending, isSuccess } = useRerunFailedSteps();

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  useEffect(() => {
    if (!isPending) {
      setSubmitted(false);
    }
  }, [isPending]);

  const submitDisabled = isPending || submitted;

  return (
    <StyledModal opened={opened} onClose={onClose} title="Rerun failed or blocked steps">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          This will rerun failed steps, blocked steps waiting for input, and any downstream steps that depend on them.
        </Text>
        <Alert color="blue" title="Structured responses are step-specific">
          Feedback is not applied by provided-code steps. Resolve an eligible paused step from its individual rerun
          action instead.
        </Alert>
        <StyledTextArea
          label="Feedback for eligible failed or blocked target steps"
          placeholder="Provide feedback that should apply to generated-code or other feedback-capable target steps"
          value={targetStepFeedback}
          onChange={(event) => setTargetStepFeedback(event.currentTarget.value)}
          autosize={true}
          minRows={3}
        />
        <StyledCheckbox
          size="xs"
          label="Run without using cached results"
          checked={ignoreCache}
          onChange={(event) => setIgnoreCache(event.currentTarget.checked)}
        />
        <CacheBypassNotice enabled={ignoreCache} />
      </Stack>
      <ModalConfirmationButtons
        onClose={onClose}
        onConfirm={() => {
          if (submitDisabled) {
            return;
          }

          setSubmitted(true);

          notifications.show({
            id: `rerun-${run.id}`,
            title: 'Rerunning failed or blocked steps',
            message: 'Rerunning failed or blocked steps, please wait...',
            color: 'green',
          });

          rerunFailedSteps({
            runId: run.id,
            ignoreCache,
            targetStepFeedback,
            expectedBaseExecutionId: run.latest_execution?.id ?? null,
          });
        }}
        confirmText="Rerun failed or blocked steps"
        confirmDisabled={submitDisabled}
        closeOnConfirm={false}
        confirmLoading={isPending || submitted}
      />
    </StyledModal>
  );
};

export default RerunFailedStepsModal;
